import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {Alert} from '@mui/material';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export type ToastOptions = {
    id?: string;
    variant?: ToastVariant;
    // milliseconds; 0 = persistent; default 6000
    timeout?: number;
    action?: React.ReactNode;
};

type Toast = {
    id: string;
    message: React.ReactNode;
    variant: ToastVariant;
    timeout: number;
    action?: React.ReactNode;
    exiting?: boolean;
};

export type ToastContextValue = {
    toast: (message: React.ReactNode, options?: ToastOptions) => string;
    dismiss: (id: string) => void;
    dismissAll: () => void;
};

const DEFAULT_TIMEOUT = 6000;
const EXIT_MS = 200;

const ToastContext = React.createContext<ToastContextValue | null>(null);

export const useToast = () => {
    const ctx = useContext(ToastContext);
    if(!ctx) throw new Error('useToast must be used within a ToastProvider');
    return ctx;
};

let nextAutoId = 0;

export const ToastProvider = (props: { children: React.ReactNode }) => {
    // The ref is the source of truth (synchronous across back-to-back calls);
    // state is the render mirror.
    const toastsRef = useRef<Toast[]>([]);
    const [toasts, setToasts] = useState<Toast[]>([]);
    const timersRef = useRef<Map<string, any>>(new Map());
    const exitTimersRef = useRef<Map<string, any>>(new Map());

    const render = () => setToasts(toastsRef.current);

    const clearTimer = (id: string) => {
        const t = timersRef.current.get(id);
        if(t) {
            clearTimeout(t);
            timersRef.current.delete(id);
        }
    };

    const clearExitTimer = (id: string) => {
        const t = exitTimersRef.current.get(id);
        if(t) {
            clearTimeout(t);
            exitTimersRef.current.delete(id);
        }
    };

    const dismiss = useCallback((id: string) => {
        const i = toastsRef.current.findIndex(x => x.id === id);
        if(i < 0) return;
        clearTimer(id);
        toastsRef.current[i] = {...toastsRef.current[i], exiting: true};
        render();
        exitTimersRef.current.set(id, setTimeout(() => {
            exitTimersRef.current.delete(id);
            toastsRef.current = toastsRef.current.filter(x => x.id !== id);
            render();
        }, EXIT_MS));
    }, []);

    const dismissAll = useCallback(() => {
        const ids = toastsRef.current.map(x => x.id);
        if(!ids.length) return;
        ids.forEach(clearTimer);
        toastsRef.current = toastsRef.current.map(x => ({...x, exiting: true}));
        render();
        ids.forEach(id => exitTimersRef.current.set(id, setTimeout(() => {
            exitTimersRef.current.delete(id);
            toastsRef.current = toastsRef.current.filter(x => x.id !== id);
            render();
        }, EXIT_MS)));
    }, []);

    const toast = useCallback((message: React.ReactNode, options: ToastOptions = {}) => {
        const id = options.id ?? `toast-${nextAutoId++}`;
        const timeout = options.timeout ?? DEFAULT_TIMEOUT;
        const variant = options.variant ?? 'info';
        const i = toastsRef.current.findIndex(x => x.id === id);
        if(i >= 0) {
            // In-place update: the toast stays mounted, the auto-hide timer resets.
            clearExitTimer(id);
            toastsRef.current[i] = {
                ...toastsRef.current[i],
                message,
                variant,
                timeout,
                action: options.action !== undefined ? options.action : toastsRef.current[i].action,
                exiting: false,
            };
        } else {
            toastsRef.current = [...toastsRef.current, {id, message, variant, timeout, action: options.action}];
        }
        render();
        clearTimer(id);
        if(timeout > 0) {
            timersRef.current.set(id, setTimeout(() => dismiss(id), timeout));
        }
        return id;
    }, [dismiss]);

    useEffect(() => {
        const timers = timersRef.current;
        const exitTimers = exitTimersRef.current;
        return () => {
            timers.forEach(t => clearTimeout(t));
            exitTimers.forEach(t => clearTimeout(t));
        };
    }, []);

    return (
        <ToastContext.Provider value={{toast, dismiss, dismissAll}}>
            {props.children}
            <div className="daybook-toasts">
                {toasts.map(t => (
                    <div key={t.id} className={'daybook-toast'+(t.exiting ? ' daybook-toast--exiting' : '')}>
                        <Alert severity={t.variant} onClose={() => dismiss(t.id)}>
                            {t.message}
                            {t.action ? <span className="daybook-toast__action">{t.action}</span> : null}
                        </Alert>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
